import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { NgClass } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { WizardStepConfig, WizardStepState } from '../ai-workspace-setup.models';
import { REQUIRED_TRIAL_REPRESENTATIVES, REQUIRED_TRIAL_TRACKED_CALLS } from '../../../../containers/default-layout/trial-reward';

const TRAINING_SLIDES_ID = '1SBGnqVMqXsDI9MaeXflHRQ-Si19Wwxa-paR8vLHb62g';
const TRAINING_SLIDES_VIEW_URL = `https://docs.google.com/presentation/d/${TRAINING_SLIDES_ID}/edit?usp=sharing`;
const TRAINING_SLIDES_DOWNLOAD_URL = `https://docs.google.com/presentation/d/${TRAINING_SLIDES_ID}/export/pptx`;
export const SALESMAX_ANDROID_APP_URL = 'https://play.google.com/store/apps/details?id=ai.salesmax';

@Component({
  selector: 'ws-finish',
  templateUrl: './ws-finish.component.html',
  styleUrls: ['./ws-finish.component.scss'],
  imports: [NgClass, MatButton]
})
export class WsFinishComponent implements OnDestroy {
  @Input() stepStates: WizardStepState[] = [];
  @Input() steps: WizardStepConfig[] = [];
  @Input() invitedUserIds: string[] = [];
  @Output() finished = new EventEmitter<void>();
  @Output() reviewStep = new EventEmitter<number>();

  @ViewChild('slidesContainer') slidesContainer: ElementRef<HTMLDivElement>;

  trainingSlidesUrl: SafeResourceUrl;
  trainingDownloadUrl = TRAINING_SLIDES_DOWNLOAD_URL;
  appStoreUrl = SALESMAX_ANDROID_APP_URL;
  showTraining = false;
  isFullscreen = false;
  linkCopied = false;
  appLinkCopied = false;
  readonly requiredRepresentatives = REQUIRED_TRIAL_REPRESENTATIVES;
  readonly requiredTrackedCalls = REQUIRED_TRIAL_TRACKED_CALLS;
  private readonly fullscreenChangeListener = () => {
    this.isFullscreen = !!document.fullscreenElement;
  };

  constructor(private sanitizer: DomSanitizer) {
    this.trainingSlidesUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://docs.google.com/presentation/d/${TRAINING_SLIDES_ID}/embed?start=false&loop=false&delayms=5000`
    );

    document.addEventListener('fullscreenchange', this.fullscreenChangeListener);
  }

  ngOnDestroy(): void {
    document.removeEventListener('fullscreenchange', this.fullscreenChangeListener);
  }

  get completedCount(): number {
    return this.steps.reduce((count, step, index) =>
      step.key !== 'finish' && this.stepStates[index]?.completed ? count + 1 : count, 0);
  }

  get totalSetupSteps(): number {
    return this.steps.filter(step => step.key !== 'finish').length;
  }

  get skippedCount(): number {
    return this.steps.reduce((count, step, index) =>
      step.key !== 'finish' && this.stepStates[index]?.skipped ? count + 1 : count, 0);
  }

  get teamStepIndex(): number {
    return this.steps.findIndex(step => step.key === 'team');
  }

  get teamSetupIncomplete(): boolean {
    return this.invitedRepresentativeCount < this.requiredRepresentatives;
  }

  get invitedRepresentativeCount(): number {
    const invitedCount = new Set((this.invitedUserIds || []).filter(Boolean)).size;
    if (invitedCount > 0) return Math.min(invitedCount, this.requiredRepresentatives);
    const index = this.teamStepIndex;
    return index >= 0 && this.stepStates[index]?.completed ? this.requiredRepresentatives : 0;
  }

  getStepStatus(index: number): string {
    if (this.stepStates[index]?.completed) return 'completed';
    if (this.stepStates[index]?.skipped) return 'skipped';
    return 'pending';
  }

  goToDashboard() {
    this.finished.emit();
  }

  reviewTeamSetup() {
    const index = this.teamStepIndex;
    if (index >= 0) this.reviewStep.emit(index);
  }

  async copyAppLink(): Promise<void> {
    try {
      await navigator.clipboard.writeText(SALESMAX_ANDROID_APP_URL);
      this.appLinkCopied = true;
      setTimeout(() => this.appLinkCopied = false, 2000);
    } catch {
      window.open(SALESMAX_ANDROID_APP_URL, '_blank', 'noopener');
    }
  }

  toggleFullscreen() {
    const el = this.slidesContainer?.nativeElement;
    if (!el) return;

    if (!document.fullscreenElement) {
      el.requestFullscreen().catch(() => {
        // Fallback: open in new tab if fullscreen not supported
        window.open(TRAINING_SLIDES_VIEW_URL, '_blank');
      });
    } else {
      document.exitFullscreen();
    }
  }

  copyLink() {
    navigator.clipboard.writeText(TRAINING_SLIDES_VIEW_URL).then(() => {
      this.linkCopied = true;
      setTimeout(() => this.linkCopied = false, 2000);
    });
  }
}
